/**
 * CorrectionApplicationService - 수정 적용 비즈니스 로직
 * Single Responsibility: 에러 수정 적용의 전체 프로세스 관리
 */

import type { GrammarError, Correction } from '$lib/types/grammar'
import { get } from 'svelte/store'
import { errorStore, grammarActions, textStore } from '$lib/stores/grammar'
import { textLayersActions } from '$lib/stores/textLayers'
import { aiSuggestionsStore } from '$lib/stores/aiSuggestions'
import { TextCorrectionService } from './TextCorrectionService'
import { ErrorMergeService } from './ErrorMergeService'

export interface CorrectionResult {
  success: boolean
  newText?: string
  message: string
  remainingErrors?: GrammarError[]
  appliedCount?: number
}

export class CorrectionApplicationService {
  /**
   * 대안 텍스트 적용 (AI 제안 등)
   * handleApplyAlternative 로직 통합
   */
  static applyAlternative(
    errorId: string,
    alternativeText: string,
    currentText: string
  ): CorrectionResult {
    // 1. 에러 찾기
    const errorState = get(errorStore)
    const error = errorState.errors.find(e => e.id === errorId)
    
    if (!error) {
      return {
        success: false,
        message: '오류를 찾을 수 없습니다'
      }
    }
    
    // 2. 원본 텍스트 확인
    const originalText = error.originalText || (error as any).original
    if (!originalText) {
      return {
        success: false,
        message: '원본 텍스트를 찾을 수 없습니다'
      }
    }
    
    // 3. 위치 확인
    const startPos = (error as any).startPos ?? error.position?.start
    if (startPos === undefined) {
      console.error('[CorrectionApplicationService] Error position not found:', error)
      return {
        success: false,
        message: '오류 위치를 찾을 수 없습니다'
      }
    }
    
    // 4. 수정 적용
    const result = TextCorrectionService.applyCorrection(
      currentText,
      originalText,
      alternativeText,
      startPos
    )
    
    if (!result) {
      return {
        success: false,
        message: '수정을 적용할 수 없습니다'
      }
    }
    
    // 5. 텍스트 레이어 업데이트
    textLayersActions.setCorrected(result.newText)
    
    // 6. 남은 에러 업데이트 - 현재 오류만 제거 (겹치는 오류는 유지)
    const remainingErrors = errorState.errors.filter(e => {
      // 현재 오류만 제거
      if (e.id === errorId) return false
      return true
    })
    
    const adjustedErrors = TextCorrectionService.updateErrorPositions(
      remainingErrors,
      startPos,
      result.offset
    )
    
    // 7. 에러 스토어 업데이트 (AI 병합 비활성화 - 이미 필터링됨)
    // mergeWithAI = false로 설정하여 중복 방지
    grammarActions.setErrors(adjustedErrors, false)
    
    return {
      success: true,
      newText: result.newText,
      message: '수정이 적용되었습니다',
      remainingErrors: adjustedErrors
    }
  }
  
  /**
   * 정식 수정 제안 적용
   * handleApplyCorrection 로직 통합
   */
  static applyCorrection(
    errorId: string,
    correctionId: string,
    currentText: string
  ): CorrectionResult {
    // 1. 에러와 수정 제안 찾기
    const errorState = get(errorStore)
    const error = errorState.errors.find(e => e.id === errorId)
    
    if (!error) {
      return {
        success: false,
        message: '오류를 찾을 수 없습니다'
      }
    }
    
    const correction = error.corrections.find(c => c.id === correctionId)
    if (!correction) {
      return {
        success: false,
        message: '수정 제안을 찾을 수 없습니다'
      }
    }
    
    // 2. 원본 텍스트 확인
    const originalText = error.originalText || (error as any).original
    if (!originalText) {
      return {
        success: false,
        message: '원본 텍스트를 찾을 수 없습니다'
      }
    }
    
    // 3. 위치 확인
    const startPos = (error as any).startPos ?? error.position?.start
    if (startPos === undefined) {
      console.error('[CorrectionApplicationService] Error position not found:', error)
      return {
        success: false,
        message: '오류 위치를 찾을 수 없습니다'
      }
    }
    
    // 4. 수정 적용
    const result = TextCorrectionService.applyCorrection(
      currentText,
      originalText,
      correction.text,
      startPos
    )
    
    if (!result) {
      return {
        success: false,
        message: '수정을 적용할 수 없습니다'
      }
    }
    
    // 5. 텍스트 레이어 업데이트
    textLayersActions.setCorrected(result.newText)
    
    // 6. 남은 에러 업데이트 - 현재 오류만 제거 (겹치는 오류는 유지)
    const remainingErrors = errorState.errors.filter(e => {
      // 현재 오류만 제거
      if (e.id === errorId) return false
      return true
    })
    
    const adjustedErrors = TextCorrectionService.updateErrorPositions(
      remainingErrors,
      startPos,
      result.offset
    )
    
    // 7. 에러 스토어 업데이트 (AI 병합 비활성화 - 이미 필터링됨)
    // mergeWithAI = false로 설정하여 중복 방지
    grammarActions.setErrors(adjustedErrors, false)
    
    return {
      success: true,
      newText: result.newText,
      message: '수정이 적용되었습니다',
      remainingErrors: adjustedErrors
    }
  }
  
  /**
   * 모든 수정 적용
   */
  static applyAllCorrections(currentText: string): CorrectionResult {
    const errorState = get(errorStore)
    const aiState = get(aiSuggestionsStore)
    
    // 무시되지 않은 에러만 필터링
    const errorsToFix = errorState.errors.filter(
      error => !errorState.ignoredErrors.has(error.id)
    )
    
    if (errorsToFix.length === 0) {
      return {
        success: false,
        message: '적용할 오류가 없습니다'
      }
    }
    
    // 원본 유지로 처리할 오류들을 찾기
    // 1. 사용자가 수동으로 원본 유지를 선택한 경우
    // 2. AI가 원본 유지를 제안한 경우
    const errorsToKeepOriginal: string[] = []
    const errorsWithAI = errorsToFix.map(error => {
      // aiSuggestionsStore에서 AI 코멘트 가져오기
      const aiComment = aiState.comments.get(error.id) || error.aiComment
      
      // 이미 원본 유지로 설정된 경우
      if (error.isOriginalKept || error.userAction === 'kept-original') {
        errorsToKeepOriginal.push(error.id)
      }
      // AI가 원본 유지를 제안한 경우
      else if (aiComment && !aiComment.agreesWithCorrection) {
        errorsToKeepOriginal.push(error.id)
      }
      
      return {
        ...error,
        aiComment
      }
    })
    
    // 모든 수정 적용 (AI가 동의한 것만 적용됨)
    const { newText, appliedCount, positionOffsets } = TextCorrectionService.applyAllCorrectionsWithOffsets(
      currentText,
      errorsWithAI  // AI 코멘트가 병합된 오류 배열 전달
    )
    
    // IMPORTANT: Clear errors BEFORE updating text to prevent race conditions
    // 원본 유지된 항목들은 계속 표시되어야 함
    const remainingErrors: GrammarError[] = errorsWithAI
      .filter(error => errorsToKeepOriginal.includes(error.id))
      .map(error => {
        // Update positions based on the corrections that were applied
        const startPos = (error as any).startPos ?? error.position?.start ?? 0
        const endPos = (error as any).endPos ?? error.position?.end ?? 0
        
        // Calculate offset for this error's position
        let offset = 0
        if (positionOffsets) {
          for (const [pos, off] of positionOffsets) {
            if (pos < startPos) {
              offset += off
            }
          }
        }
        
        return {
          ...error,
          position: {
            start: startPos + offset,
            end: endPos + offset
          },
          startPos: startPos + offset,
          endPos: endPos + offset,
          isOriginalKept: true,
          userAction: 'kept-original' as const
        }
      })
    
    // 에러 스토어 업데이트 (원본 유지된 항목들만 남김)
    grammarActions.setErrors(remainingErrors, false)
    
    // 텍스트 레이어 업데이트 - do this AFTER clearing errors
    textLayersActions.setCorrected(newText)
    
    const keptCount = errorsToKeepOriginal.length
    const totalProcessed = appliedCount + keptCount
    
    return {
      success: true,
      newText,
      message: `${appliedCount}개 수정, ${keptCount}개 원본 유지 (총 ${totalProcessed}개 처리)`,
      remainingErrors,
      appliedCount: totalProcessed  // Return total processed count
    }
  }
}